
import React from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Clock, CheckCircle, XCircle, FileText, AlertCircle } from 'lucide-react';

type VerificationStatus = 'all' | 'pending' | 'waiting' | 'approved' | 'rejected';

interface DocumentVerificationFiltersProps {
  statusFilter: VerificationStatus;
  onStatusFilterChange: (status: VerificationStatus) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusCounts: Record<string, number>;
}

const DocumentVerificationFilters: React.FC<DocumentVerificationFiltersProps> = ({
  statusFilter,
  onStatusFilterChange,
  searchTerm,
  onSearchChange,
  statusCounts
}) => {
  const statusOptions = [
    { 
      value: 'all' as const, 
      label: 'Todos', 
      icon: FileText, 
      color: 'bg-white/10 text-white hover:bg-white/20',
      count: Object.values(statusCounts).reduce((sum, count) => sum + count, 0)
    },
    { 
      value: 'waiting' as const, 
      label: 'Aguardando', 
      icon: Clock, 
      color: 'bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30',
      count: statusCounts.waiting || 0
    },
    { 
      value: 'pending' as const, 
      label: 'Incompletos', 
      icon: AlertCircle, 
      color: 'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30',
      count: statusCounts.pending || 0
    },
    { 
      value: 'approved' as const, 
      label: 'Aprovados', 
      icon: CheckCircle, 
      color: 'bg-green-500/20 text-green-300 hover:bg-green-500/30',
      count: statusCounts.approved || 0
    },
    { 
      value: 'rejected' as const, 
      label: 'Rejeitados', 
      icon: XCircle, 
      color: 'bg-red-500/20 text-red-300 hover:bg-red-500/30',
      count: statusCounts.rejected || 0
    }
  ];

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
        <Input
          type="text"
          placeholder="Buscar por usuário..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 bg-white/5 border-white/20 text-white placeholder:text-white/60 focus:border-luxury-gold/50"
        />
      </div>

      {/* Status Filters */}
      <div className="flex flex-wrap gap-2">
        {statusOptions.map((option) => {
          const Icon = option.icon;
          const isActive = statusFilter === option.value;
          
          return (
            <button
              key={option.value}
              onClick={() => onStatusFilterChange(option.value)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
                ${isActive 
                  ? 'bg-luxury-gold text-black' 
                  : option.color
                }
                ${option.count === 0 ? 'opacity-50' : ''}
              `}
              disabled={option.count === 0}
            >
              <Icon className="h-4 w-4" />
              <span>{option.label}</span>
              {option.count > 0 && (
                <Badge 
                  variant="secondary" 
                  className={`
                    ml-1 h-5 min-w-[20px] text-xs
                    ${isActive 
                      ? 'bg-black/20 text-black' 
                      : 'bg-white/20 text-white'
                    }
                  `}
                >
                  {option.count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentVerificationFilters;
